var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import dayjs from "dayjs";
import moment from "moment";
import { DatepickerType, DatepickerMode, } from "./types";
export const isDateRangePicker = (props) => {
    return props.type === DatepickerType.Range;
};
const toDayjs = (date, useNativeDates) => {
    if (useNativeDates) {
        return dayjs(date);
    }
    else {
        return dayjs(date.toDate());
    }
};
const fromDayjs = (date, useNativeDates) => {
    if (useNativeDates) {
        return date.toDate();
    }
    else {
        return moment(date.toDate());
    }
};
export const propsAdapter = (props) => {
    const { value, defaultValue, onChange, format, minDate, maxDate, disabledDate, useNativeDates } = props, otherProps = __rest(props, ["value", "defaultValue", "onChange", "format", "minDate", "maxDate", "disabledDate", "useNativeDates"]);
    const adaptedOnChange = onChange &&
        ((date) => {
            if (!date)
                return onChange(null, "");
            // this `if` is only necessary for proper TS inference of fromDayjs return type without having to cast
            if (useNativeDates) {
                return props.onChange(fromDayjs(date, true), date.format(format));
            }
            else {
                return props.onChange(fromDayjs(date, false), date.format(format));
            }
        });
    const adaptedDisabledDate = disabledDate &&
        ((date) => {
            // this `if` is only necessary for proper TS inference of fromDayjs return type without having to cast
            if (useNativeDates) {
                return props.disabledDate(fromDayjs(date, true));
            }
            else {
                return props.disabledDate(fromDayjs(date, false));
            }
        });
    return Object.assign({ value: value && toDayjs(value, useNativeDates), defaultValue: defaultValue && toDayjs(defaultValue, useNativeDates), minDate: minDate && toDayjs(minDate, useNativeDates), maxDate: maxDate && toDayjs(maxDate, useNativeDates), onChange: adaptedOnChange, disabledDate: adaptedDisabledDate, format }, otherProps);
};
export const validateDate = (date, validation) => {
    const { minDate, maxDate, disablePast, disableFuture, disabledDate, format, useNativeDates } = validation;
    if (disabledDate && disabledDate(date))
        return false;
    const dayjsDate = dayjs.isDayjs(date) ? date : toDayjs(date, useNativeDates);
    const dayjsMinDate = minDate && (dayjs.isDayjs(minDate) ? minDate : toDayjs(minDate, useNativeDates));
    const dayjsMaxDate = maxDate && (dayjs.isDayjs(maxDate) ? maxDate : toDayjs(maxDate, useNativeDates));
    // It is important to format with the given format here.
    // For instance, a format of "YYYY-MM-DD" will ignore the time part
    // whereas a format of "YYYY-MM-DD HH:ss" will consider the time part
    // for comparison
    const now = dayjs(dayjs().format(format));
    const value = dayjs(dayjsDate.format(format));
    const min = dayjsMinDate && dayjs(dayjsMinDate.format(format));
    const max = dayjsMaxDate && dayjs(dayjsMaxDate.format(format));
    if (!value.isValid())
        return false;
    if (min && value < min)
        return false;
    if (max && value > max)
        return false;
    if (disablePast && value < now)
        return false;
    if (disableFuture && value > now)
        return false;
    return true;
};
/** Returns MUI picker functions to disable date and time
 *  For DateTime picker, returns adapted MUI functions to have the proper behaviour
 *  What we wish to accomplish here is we don't want to disable a date when there is
 *  an enabled hour for that date, and we don't want to disable an hour when there is
 *  an enabled minute for that hour
 */
export const getDisabledDateAndTimeMUIProps = ({ picker, timeSteps, disabledDate, }) => {
    if (picker !== DatepickerMode.DateTime) {
        return { shouldDisableDate: disabledDate };
    }
    // If the given dateTime has enabled minutes within the given hour
    const hasEnabledMinutes = (date) => {
        for (let minute = 0; minute < 60; minute += timeSteps.minutes) {
            if (!(disabledDate === null || disabledDate === void 0 ? void 0 : disabledDate(date.set("minute", minute)))) {
                return true;
            }
        }
        return false;
    };
    // If the given dateTime has enabled hours within the given day
    const hasEnabledHours = (date) => {
        for (let hour = 0; hour < 24; hour += timeSteps.hours) {
            const dateTime = date.set("hour", hour);
            if (hasEnabledMinutes(dateTime)) {
                return true;
            }
        }
        return false;
    };
    const shouldDisableDate = disabledDate &&
        ((date) => {
            return !hasEnabledHours(date);
        });
    const shouldDisableTime = disabledDate &&
        ((dateTime, view) => {
            if (view === "hours") {
                return !hasEnabledMinutes(dateTime);
            }
            else if (view === "minutes") {
                return disabledDate(dateTime);
            }
            return false;
        });
    return {
        shouldDisableTime,
        shouldDisableDate,
    };
};
//# sourceMappingURL=helpers.js.map